export type DayKey =
  | "mon"
  | "tue"
  | "wed"
  | "thu"
  | "fri"
  | "sat"
  | "sun"
  | "holiday";

export const DAY_ORDER: DayKey[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
  "holiday",
];

export const DAY_LABEL_SHORT: Record<DayKey, string> = {
  mon: "Lun",
  tue: "Mar",
  wed: "Mié",
  thu: "Jue",
  fri: "Vie",
  sat: "Sáb",
  sun: "Dom",
  holiday: "Festivos",
};

const DAY_BITS: Record<DayKey, number> = {
  mon: 1,
  tue: 2,
  wed: 4,
  thu: 8,
  fri: 16,
  sat: 32,
  sun: 64,
  holiday: 128,
};

export type ScheduleBlock = {
  days: DayKey[];
  open: string;
  close: string;
};

type Encoded = { v: 1; b: [number, string, string][] };

const MASK_MON_SAT = 63; // 1+2+4+8+16+32
const MASK_SUN_HOLIDAY = 64 + 128;

export function daysFromMask(mask: number): DayKey[] {
  const out: DayKey[] = [];
  for (const d of DAY_ORDER) {
    if (mask & DAY_BITS[d]) out.push(d);
  }
  return out;
}

export function defaultScheduleBlocks(): ScheduleBlock[] {
  return [
    { days: daysFromMask(MASK_MON_SAT), open: "10:00", close: "20:00" },
    { days: daysFromMask(MASK_SUN_HOLIDAY), open: "10:00", close: "21:00" },
  ];
}

export function maskFromDays(days: DayKey[]): number {
  let m = 0;
  for (const d of days) m |= DAY_BITS[d];
  return m;
}

export function encodeSchedule(blocks: ScheduleBlock[]): string {
  const b: [number, string, string][] = blocks.map((bl) => [
    maskFromDays(bl.days),
    bl.open,
    bl.close,
  ]);
  const payload: Encoded = { v: 1, b };
  return JSON.stringify(payload);
}

export type DecodedSchedule =
  | { kind: "structured"; blocks: ScheduleBlock[] }
  | { kind: "legacy"; text: string };

export function decodeSchedule(raw: string | null | undefined): DecodedSchedule {
  const s = raw?.trim() ?? "";
  if (!s) return { kind: "structured", blocks: defaultScheduleBlocks() };
  try {
    const o = JSON.parse(s) as Encoded;
    if (o?.v === 1 && Array.isArray(o.b)) {
      const blocks: ScheduleBlock[] = o.b.map((row) => {
        const [mask, open, close] = row;
        return {
          days: daysFromMask(Number(mask)),
          open: String(open ?? ""),
          close: String(close ?? ""),
        };
      });
      return { kind: "structured", blocks };
    }
  } catch {
    /* texto libre */
  }
  return { kind: "legacy", text: s };
}

function sortDays(keys: DayKey[]): DayKey[] {
  const rank = new Map(DAY_ORDER.map((d, i) => [d, i]));
  return [...keys].sort((a, b) => (rank.get(a)! - rank.get(b)!));
}

/** Agrupa Lun–Dom consecutivos como rango; festivos aparte. */
function formatWeekdaySpan(sorted: DayKey[]): string | null {
  const noHol = sorted.filter((d) => d !== "holiday");
  if (noHol.length === 0) return null;
  const idx = noHol.map((d) => DAY_ORDER.indexOf(d)).filter((i) => i >= 0 && i <= 6);
  if (idx.length !== noHol.length) return null;
  const min = Math.min(...idx);
  const max = Math.max(...idx);
  if (max - min + 1 !== noHol.length) return null;
  if (!idx.every((i, k) => i === min + k)) return null;

  const start = DAY_ORDER[min];
  const end = DAY_ORDER[max];
  if (min === max) return DAY_LABEL_SHORT[start];

  const a = DAY_LABEL_SHORT[start];
  const b = DAY_LABEL_SHORT[end];
  return `${a} a ${b}`;
}

export function formatDaysLabel(days: DayKey[]): string {
  const sorted = sortDays(days);
  if (sorted.length === 0) return "";

  const hasHol = sorted.includes("holiday");
  const weekOnly = sorted.filter((d) => d !== "holiday");

  if (hasHol && weekOnly.length === 1 && weekOnly[0] === "sun") {
    return "Domingos y Festivos";
  }
  if (hasHol && weekOnly.length === 0) {
    return "Festivos";
  }

  const span = formatWeekdaySpan(sorted.filter((d) => d !== "holiday"));
  if (span && hasHol) {
    return `${span} y Festivos`;
  }
  if (span) return span;

  return sorted.map((d) => DAY_LABEL_SHORT[d]).join(", ");
}

export function formatTimeHm(hm: string): string {
  const m = hm.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return hm;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ap}`;
}

export function formatScheduleHuman(raw: string | null | undefined): string {
  const d = decodeSchedule(raw);
  if (d.kind === "legacy") return d.text;
  return d.blocks
    .filter((b) => b.days.length && b.open && b.close)
    .map((b) => {
      const days = formatDaysLabel(b.days);
      return `${days}: ${formatTimeHm(b.open)} a ${formatTimeHm(b.close)}`;
    })
    .join(" · ");
}

export function validateSchedulePayload(raw: string | null | undefined): string {
  const d = decodeSchedule(raw);
  if (d.kind === "legacy") {
    if (!d.text.trim()) return "Indique el horario o use el modo texto.";
    return "";
  }
  if (d.blocks.length === 0) return "Añada al menos una franja horaria.";
  for (let i = 0; i < d.blocks.length; i++) {
    const b = d.blocks[i];
    if (!b.days.length) return `Franja ${i + 1}: seleccione al menos un día.`;
    if (!b.open || !b.close) return `Franja ${i + 1}: indique apertura y cierre.`;
  }
  return "";
}
